import { makeRules, seededRng, type BotLevel, type Rng, type RulesConfig } from '@mendikot/engine';
import { createRoom, handle, type EncodedCore } from '@mendikot/host';
import {
  decodePrivateView,
  decodePublicView,
  seatKey,
  type CommandMeta,
  type Envelope,
  type Reply,
  type RoomPrivateView,
  type RoomPublicView,
} from '@mendikot/protocol';
import type { CommandRequest, GameGateway, PresenceState, Unsubscribe } from '../services';

export interface PracticeOptions {
  uid: string;
  name: string;
  avatarId: string;
  rules?: Partial<RulesConfig>;
  botLevel?: BotLevel;
  seed?: string;
  /** Previously saved table, to resume after a reload. */
  restore?: EncodedCore;
  onCommit?: (core: EncodedCore) => void;
}

export const PRACTICE_ROOM_ID = 'practice';

/**
 * Practice mode runs the exact authoritative host (`@mendikot/host`) in memory with three bots,
 * behind the same GameGateway interface as online play — so the UI cannot tell the difference.
 */
export class LocalGameGateway implements GameGateway {
  readonly kind = 'practice' as const;
  readonly seed: string;
  private core: EncodedCore;
  private readonly rng: Rng;
  private readonly publicListeners = new Set<(v: RoomPublicView | null) => void>();
  private readonly privateListeners = new Map<number, Set<(v: RoomPrivateView | null) => void>>();

  constructor(private readonly opts: PracticeOptions) {
    this.seed = opts.seed ?? Math.random().toString(36).slice(2, 10);
    this.rng = seededRng(this.seed);
    if (opts.restore) {
      this.core = opts.restore;
      return;
    }
    const rules = makeRules({ actionTimeoutSec: 0, ...opts.rules });
    this.core = createRoom({
      roomId: PRACTICE_ROOM_ID,
      code: 'PRACTC',
      host: { uid: opts.uid, name: opts.name, avatarId: opts.avatarId },
      rules,
      now: Date.now(),
    }).core;
    for (let seat = 1; seat < rules.playerCount; seat++) {
      this.apply({ type: 'seat.addBot', roomId: PRACTICE_ROOM_ID, payload: { seat } });
    }
  }

  /** Current stored table (for persistence across reloads). */
  snapshot(): EncodedCore {
    return this.core;
  }

  async send(request: CommandRequest, meta: CommandMeta): Promise<Reply> {
    return this.apply(request, meta);
  }

  private apply(request: CommandRequest, meta?: CommandMeta): Reply {
    const env = {
      ...request,
      meta: meta ?? {
        requestId: `local-${Math.random().toString(36).slice(2)}`,
        appVersion: 'local',
        platform: 'local',
      },
    } as Envelope;
    const result = handle(this.core, env, {
      actor: { kind: 'user', uid: this.opts.uid },
      now: Date.now(),
      rng: this.rng,
      botLevel: this.opts.botLevel ?? 'normal',
    });
    if (result.kind === 'commit') {
      this.core = result.core;
      this.opts.onCommit?.(this.core);
      this.notify();
    }
    return result.reply;
  }

  private notify(): void {
    const pub = decodePublicView(this.core.public);
    for (const listener of this.publicListeners) listener(pub);
    for (const [seat, listeners] of this.privateListeners) {
      const priv = decodePrivateView(this.core.private?.[seatKey(seat)]);
      for (const listener of listeners) listener(priv);
    }
  }

  subscribePublic(_roomId: string, listener: (view: RoomPublicView | null) => void): Unsubscribe {
    this.publicListeners.add(listener);
    queueMicrotask(() => listener(decodePublicView(this.core.public)));
    return () => this.publicListeners.delete(listener);
  }

  subscribePrivate(
    _roomId: string,
    seat: number,
    listener: (view: RoomPrivateView | null) => void,
  ): Unsubscribe {
    const set = this.privateListeners.get(seat) ?? new Set();
    set.add(listener);
    this.privateListeners.set(seat, set);
    queueMicrotask(() => listener(decodePrivateView(this.core.private?.[seatKey(seat)])));
    return () => set.delete(listener);
  }

  watchPresence(
    _roomId: string,
    listener: (presence: Record<string, PresenceState>) => void,
  ): Unsubscribe {
    listener({ [this.opts.uid]: { connected: true, visible: true, at: Date.now() } });
    return () => undefined;
  }

  joinPresence(): Unsubscribe {
    return () => undefined;
  }

  setVisible(): void {}

  onConnectionChange(listener: (connected: boolean) => void): Unsubscribe {
    listener(true);
    return () => undefined;
  }

  serverNow(): number {
    return Date.now();
  }
}
